import { ChargePointAccordionList } from '@/features/charging/components/layout/ChargePointAccordionList'
import { useChargeTerminalDetails } from '@/features/charging/hooks/useChargeTerminalDetails'
import DropDownSelectZone from '@/features/charging/components/ui/DropDownSelectZone'
import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { useCompany } from '@/contexts'
import type { ChargePointHardwareStatusEnum } from '@/features/charging/api/ChargePoint-details.data'
import type {
  ChargeTerminalHardwareStatus,
  ChargeTerminalNetworkStatus
} from '@/features/charging/api/chargeTerminal-list.data'
import z from 'zod'
import { useFormBuilderSearch } from '@/components/FormBuilder/formBuilderSearch.ts'
import { PageSearchFilter } from '@/components/PageSearchFilter/PageSearchFilter.tsx'

export const Route = createFileRoute(
  '/_authenticated/$companySlug/charging-infrastructure/$siteId/terminals/$chargeTerminalId'
)({
  component: ChargeTerminalId,
  head: ({ params }) => ({
    meta: [{ title: `Charge Terminal ${params.chargeTerminalId}` }]
  }),
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['CHARGER_READ'])
  }
})

function ChargeTerminalId() {
  const { id: companyId, sites } = useCompany()
  const { t } = useTranslation(['@feature_charging', '_routes'])
  const [searchQuery, setSearchQuery] = useState('')
  const { siteId, chargeTerminalId } = Route.useParams()
  const site = sites.find(s => s.id === siteId)
  const [siteName, setSiteName] = React.useState<string>(site?.name || 'Unnamed location')

  const chargeTerminalDetails = useChargeTerminalDetails(
    companyId,
    siteId,
    chargeTerminalId?.toString() ?? ''
  )

  // Create an array of objects with id and hardwareStatus for each charge point
  const chargePointIdList: { id: string; status?: ChargePointHardwareStatusEnum }[] =
    Array.isArray(chargeTerminalDetails.data?.chargePoints)
      ? chargeTerminalDetails.data.chargePoints
          .filter(chargePoint => chargePoint?.id != null)
          .map(point => ({
            id: point.id.toString(),
            status: point.hardwareStatus ?? undefined
          }))
      : []

  // Set default selection and redirect when data loads
  React.useEffect(() => {
    setSiteName(site?.name || 'Unnamed location')
  }, [site, siteName])

  // Handle hub selection change (DropDownSelectZone returns selected label)
  const handleChange = (selectedName: string) => {
    setSiteName(selectedName)
  }

  const chargeTerminalHardwareStatus: ChargeTerminalHardwareStatus =
    chargeTerminalDetails.data?.hardwareStatus ?? 'N/A'

  const chargeTerminalNetworkStatus: ChargeTerminalNetworkStatus =
    chargeTerminalDetails.data?.networkStatus ?? 'N/A'

  // TODO this is not connected to backend
  // TODO link default values
  const filterSchema = z.object({
    timeRange: z
      .tuple([
        z.coerce.date<Date>().meta({ label: 'start', withTime: false }).optional(),
        z.coerce.date<Date>().meta({ label: 'end', withTime: false }).optional()
      ])
      .optional()
      .describe(
        t('_routes:routes.$chargingHubId_terminals_$chargeTerminalId.filter.timeRange')
      )
  })

  const { values: filterValues, persistOnSubmit } = useFormBuilderSearch(filterSchema)

  return (
    <>
      <Box>
        <PageHeader
          backArrow={{
            // @ts-expect-error - url is valid but TypeScript doesn't recognize it as a valid route
            to: '/$companySlug/charging-infrastructure/$siteId/terminals',
            params: { siteId: String(siteId) },
            from: Route.fullPath
          }}
        >
          {chargeTerminalDetails.data?.name ?? 'Loading...'}
        </PageHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle3" color="grey.A700">
            {t(
              '_routes:routes.$chargingHubId_terminals_$chargeTerminalId.main.hardwareStatus',
              {
                hardwareStatusValue: t(
                  `@feature_charging:api.data.hardwareStatus.${chargeTerminalHardwareStatus}`
                )
              }
            )}
          </Typography>
          <Typography variant="subtitle3" color="grey.A700">
            |
          </Typography>
          <Typography variant="subtitle3" color="grey.A700">
            {t(
              '_routes:routes.$chargingHubId_terminals_$chargeTerminalId.main.networkStatus',
              {
                networkStatusValue: t(
                  `@feature_charging:api.data.networkStatus.${chargeTerminalNetworkStatus}`
                )
              }
            )}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 28 }} /> {/* Spacer Box */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          my: 2
        }}
      >
        <PageSearchFilter
          textFilter={{
            value: searchQuery,
            onChange: setSearchQuery,
            placeholder: t(
              '_routes:routes.$chargingHubId_terminals_$chargeTerminalId.main.searchPlaceholder'
            )
          }}
          buttonFilter={{
            schema: filterSchema,
            onSubmit: persistOnSubmit(),
            defaultValues: filterValues
          }}
        />
        <DropDownSelectZone
          id="charging-hub-selector"
          value={siteName}
          onChange={handleChange}
          options={[
            { id: String(site?.id ?? ''), label: site?.name ?? 'Unnamed location' }
          ]}
          placeholder={t(
            '_routes:routes.$chargingHubId_terminals_$chargeTerminalId.main.disabledSiteSelectorLabel'
          )}
          sx={{ display: 'inline-flex', minWidth: 262, height: 40 }}
          disabled={true}
        />
      </Box>
      <Box sx={{ height: 8 }} /> {/* Spacer Box */}
      <ChargePointAccordionList
        companyId={companyId}
        siteId={siteId}
        chargeTerminalId={chargeTerminalId}
        chargePointList={chargePointIdList}
        startDateRange={{
          startAfter: filterValues.timeRange?.[0]
            ? (filterValues.timeRange[0] as Date)
            : undefined,
          startBefore: filterValues.timeRange?.[1]
            ? (filterValues.timeRange[1] as Date)
            : undefined
        }}
      />
    </>
  )
}
